import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import type { SafeString } from '@ember/template';
import { tracked } from '@glimmer/tracking';

import moment from 'moment';
import { hashSettled } from 'rsvp';

import type { PopupData, Service } from 'webapp-types';
import { BillingFeature } from 'webapp/enums/enums';
import { sortBy } from 'webapp/utils/common';

import BarLoaderComponent from 'webapp/components/bar-loader';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import SubmitButtonComponent, { IconPosition } from 'webapp/components/submit-button';

enum FeatureType {
	DATABOARDS = 'databoards',
	REPORTS = 'reports',
	CUSTOM_METRICS = 'custom_metrics',
	DATA_CALCULATIONS = 'data_calculations',
	SNAPSHOTS = 'snapshots',
	SCORECARDS = 'scorecards',
	ALERTS = 'alerts',
	GOALS = 'goals',
	CLIENTS = 'clients',
	USERS = 'users',
	SOURCES = 'sources',
}

type UsedFeatures = Partial<Record<FeatureType, number>>;

interface FeatureInfo {
	icon: IconName;
	usedCount: number;
	text: string;
	baseColor: string;
	fillColor: string;
	backgroundColor: SafeString;
	isShown: boolean;
}

export default class AccountBillingCancellationFlowPopupSomethingWrongComponent extends PopupInnerModalComponent<PopupData.None> {
	@service('account') declare accountService: Service.Account;
	@service('billing') declare billingService: Service.Billing;
	@service('flow/cancellation') declare flowCancellationService: Service.Flow.Cancellation;
	@service('model-manager/alerts') declare mmAlertsService: Service.ModelManager.Alerts;
	@service('model-manager/board-shares')
	declare mmBoardSharesService: Service.ModelManager.BoardShares;
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;
	@service('model-manager/report-schedule-settings')
	declare mmReportScheduleSettingsService: Service.ModelManager.ReportScheduleSettings;
	@service('model-manager/scorecards')
	declare mmScorecardsService: Service.ModelManager.Scorecards;
	@service('model-manager/scripts') declare mmScriptsService: Service.ModelManager.Scripts;
	@service('model-manager/spaces') declare mmSpacesService: Service.ModelManager.Spaces;
	@service('request') declare requestService: Service.Request;

	@tracked currentFeatures: UsedFeatures = {};
	@tracked isAccountReviewOnly = false;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.flowCancellationService.triggerCancellationFlowStartedEvent();
		this.submitInProgress = true;

		this.currentFeatures = {
			[FeatureType.DATABOARDS]: this.billingService.getFeatureCurrent(BillingFeature.BOARDS),
			[FeatureType.CUSTOM_METRICS]: this.billingService.getFeatureCurrent(
				BillingFeature.CUSTOM_METRICS,
			),
			[FeatureType.CLIENTS]: this.mmSpacesService.otherSpaceRecords.length,
			[FeatureType.USERS]: this.billingService.getFeatureCurrent(BillingFeature.USERS),
			[FeatureType.SOURCES]: this.billingService.getFeatureCurrent(BillingFeature.SOURCES),
		};

		const promises: Record<string, Promise<unknown>> = {};
		promises[FeatureType.REPORTS] = this.mmReportsService.requestReports().then(() => {
			this.currentFeatures = {
				...this.currentFeatures,
				[FeatureType.REPORTS]: this.mmReportsService.savedRecords.length,
			};
		});

		promises[FeatureType.DATA_CALCULATIONS] = this.mmScriptsService
			.requestScripts()
			.then(() => {
				this.currentFeatures = {
					...this.currentFeatures,
					[FeatureType.DATA_CALCULATIONS]: this.mmScriptsService.savedRecords.length,
				};
			});

		promises[FeatureType.GOALS] = this.mmGoalsService.requestSpaceGoals().then(() => {
			this.currentFeatures = {
				...this.currentFeatures,
				[FeatureType.GOALS]: this.mmGoalsService.savedRecords.length,
			};
		});

		promises[FeatureType.ALERTS] = this.mmAlertsService.requestAlerts().then(() => {
			this.currentFeatures = {
				...this.currentFeatures,
				[FeatureType.ALERTS]: this.mmAlertsService.savedRecords.length,
			};
		});

		promises[FeatureType.SCORECARDS] = this.mmScorecardsService.requestScorecards().then(() => {
			this.currentFeatures = {
				...this.currentFeatures,
				[FeatureType.SCORECARDS]: this.mmScorecardsService.savedRecords.length,
			};
		});

		promises.accountReviewOnly = this.flowCancellationService
			.requestIsAccountReviewOnly()
			.then((isAccountReviewOnly) => {
				this.isAccountReviewOnly = isAccountReviewOnly;
			});

		promises[FeatureType.SNAPSHOTS] = hashSettled({
			boards: this.mmBoardSharesService.requestBoardShares(),
			reports: this.mmReportScheduleSettingsService.requestReportSchedules(),
		});

		hashSettled(promises)
			.then(() => {
				const scheduledBoardSharesCount =
						this.mmBoardSharesService.scheduledSavedRecords.length,
					scheduledReportsCount =
						this.mmReportScheduleSettingsService.savedRecords.length,
					scheduledSnapshotsCount = scheduledBoardSharesCount + scheduledReportsCount;

				this.currentFeatures = {
					...this.currentFeatures,
					[FeatureType.SNAPSHOTS]: scheduledSnapshotsCount,
				};
			})
			.finally(() => {
				this.submitInProgress = false;

				next(() => {
					this.reposition();
				});
			});
	}

	get topUsedFeatures(): FeatureInfo[] {
		const currentFeatures = this.currentFeatures,
			allCurrentFeaturesCount = Object.keys(currentFeatures).length,
			numberOfShownItems = 8;
		let usedFeatures = [];

		const databoardsCount = currentFeatures[FeatureType.DATABOARDS] || 0;
		usedFeatures.push({
			icon: 'Databoards',
			usedCount: databoardsCount,
			text: `Created <strong>${databoardsCount} dashboards</strong>`,
			baseColor: '255, 191, 28',
			isShown: databoardsCount >= 5,
		});

		const reportsCount = currentFeatures[FeatureType.REPORTS] || 0;
		usedFeatures.push({
			icon: 'Reports',
			usedCount: reportsCount,
			text: `Created <strong>${reportsCount} reports</strong>`,
			baseColor: '255, 150, 0',
			isShown: reportsCount >= 5,
		});

		const customMetricsCount = currentFeatures[FeatureType.CUSTOM_METRICS] || 0;
		usedFeatures.push({
			icon: 'Settings',
			usedCount: customMetricsCount,
			text: `Created <strong>${customMetricsCount} built metrics</strong>`,
			baseColor: '255, 73, 41',
			isShown: customMetricsCount >= 10,
		});

		const dataCalculationsCount = currentFeatures[FeatureType.DATA_CALCULATIONS] || 0;
		usedFeatures.push({
			icon: 'Databoards',
			usedCount: dataCalculationsCount,
			text: `Created <strong>${dataCalculationsCount} calculated metrics</strong>`,
			baseColor: '133, 96, 168',
			isShown: dataCalculationsCount >= 10,
		});

		const snapshotsCount = currentFeatures[FeatureType.SNAPSHOTS] || 0;
		usedFeatures.push({
			icon: 'Clock',
			usedCount: snapshotsCount,
			text: `Scheduled <strong>${snapshotsCount} snapshots</strong>`,
			baseColor: '0, 114, 188',
			isShown: snapshotsCount >= 10,
		});

		const scorecardsCount = currentFeatures[FeatureType.SCORECARDS] || 0;
		usedFeatures.push({
			icon: 'Scorecard',
			usedCount: scorecardsCount,
			text: `Created <strong>${scorecardsCount} scorecards</strong>`,
			baseColor: '46, 193, 214',
			isShown: scorecardsCount >= 10,
		});

		const alertsCount = currentFeatures[FeatureType.ALERTS] || 0;
		usedFeatures.push({
			icon: 'Notifications',
			usedCount: alertsCount,
			text: `Created <strong>${alertsCount} alerts</strong>`,
			baseColor: '29, 180, 117',
			isShown: alertsCount >= 10,
		});

		const goalsCount = currentFeatures[FeatureType.GOALS] || 0;
		usedFeatures.push({
			icon: 'OkRs',
			usedCount: goalsCount,
			text: `Created <strong>${goalsCount} goals</strong>`,
			baseColor: '132, 156, 163',
			isShown: goalsCount >= 10,
		});

		const clientsCount = currentFeatures[FeatureType.CLIENTS] || 0;
		if (this.accountService.isAgency) {
			usedFeatures.push({
				icon: 'Clients',
				usedCount: clientsCount,
				text: `Created <strong>${clientsCount} accounts</strong>`,
				baseColor: '255, 191, 28',
				isShown: clientsCount >= 5,
			});
		}

		const usersCount = currentFeatures[FeatureType.USERS] || 0;
		usedFeatures.push({
			icon: 'User',
			usedCount: usersCount,
			text: `Collaborated with <strong>${usersCount} users</strong>`,
			baseColor: '255, 150, 0',
			isShown: usersCount >= 5,
		});

		const sourcesCount = currentFeatures[FeatureType.SOURCES] || 0;
		usedFeatures.push({
			icon: 'DataManager',
			usedCount: sourcesCount,
			text: `Connected <strong>${sourcesCount} data sources</strong>`,
			baseColor: '255, 73, 41',
			isShown: sourcesCount >= 5,
		});

		// Sort by descending used count
		usedFeatures = sortBy(usedFeatures, 'usedCount').reverse();

		const usedFeatursCount = usedFeatures.filter((item) => item.usedCount).length;
		const allFeatures = [
			{
				icon: 'Grid',
				usedCount: usedFeatursCount,
				text: `Used <strong>${usedFeatursCount}</strong> out of <strong>${allCurrentFeaturesCount} features</strong>`,
				baseColor: '29, 180, 117',
				isShown: !!usedFeatursCount,
			},
			...usedFeatures,
		];

		const formattedUsedFeatures = allFeatures
			.filter((item) => item.isShown)
			.map((item) => {
				return {
					...item,
					fillColor: `rgb(${item.baseColor})`,
					backgroundColor: htmlSafe(`background-color: rgba(${item.baseColor},0.1);`),
				} as FeatureInfo;
			});

		return formattedUsedFeatures.slice(0, numberOfShownItems);
	}

	get isTopUsedFeaturesShown(): boolean {
		return this.topUsedFeatures.length > 1 && !this.billingService.isTrial;
	}

	get usersTimeWithUs(): string | null {
		const currentAccountCreatedTs = this.accountService.currentSpace?.created || 0,
			isAccountOlderThanSixMonths =
				moment().unix() >= moment.unix(currentAccountCreatedTs).add(6, 'months').unix();

		return isAccountOlderThanSixMonths
			? moment.unix(currentAccountCreatedTs).fromNow(true)
			: null;
	}

	@action
	scheduleAccountReview(): void {
		this.flowCancellationService.openCancellationMeeting(
			this.isAccountReviewOnly ? 'cancellation_review_only' : 'cancellation',
		);
	}

	@action
	cancel(): void {
		this.flowCancellationService.triggerCancelSubscriptionInitialEvent();
		this.flowCancellationService.openFeedbackPopup();
	}
	<template>
		<div class="content">
			<CommonUiIconComponent @name="Handshake" @size="2xl" />

			{{#if this.submitInProgress}}
				<div class="features features--loader-shown">
					<BarLoaderComponent @size="big" />
				</div>
			{{else}}
				{{#if this.isAccountReviewOnly}}
					<h2>Let's explore what's possible with your Databox setup</h2>
					<p>
						Before you make a final decision, we'd love to understand what's changing
						for your team and see how we could help you get more value from Databox.
					</p>
				{{else}}
					<h2>Let's review your subscription together</h2>
					<p>
						As a valued Databox customer, we'd like to offer you a personal account
						review before you make a final decision about your subscription.
					</p>
				{{/if}}

				{{#if this.isTopUsedFeaturesShown}}
					<p>
						{{#if this.usersTimeWithUs}}
							You have been with us for more than
							<strong>{{this.usersTimeWithUs}}</strong>.
						{{/if}}
						In this time you have achieved:
					</p>
				{{/if}}

				<div class="features">
					{{#if this.isTopUsedFeaturesShown}}
						<ul>
							{{#each this.topUsedFeatures as |item|}}
								<li>
									<div style={{item.backgroundColor}}>
										<CommonUiIconComponent
											@name={{item.icon}}
											@size="md"
											@fill={{item.fillColor}}
										/>
									</div>
									<span>{{{item.text}}}</span>
								</li>
							{{/each}}
						</ul>
					{{/if}}
				</div>

				{{#if this.isAccountReviewOnly}}
					<p>
						Together, we can explore how our latest AI capabilities could simplify your
						analysis and reporting, and whether a different setup or plan might better
						fit your needs.
					</p>

					<div class="controls2 controls2--center">
						<SubmitButtonComponent
							@text="Schedule your account review"
							@size="big"
							@icon="External"
							@iconPosition={{IconPosition.AFTER}}
							@noDelay={{true}}
							@onTrigger={{this.scheduleAccountReview}}
						/>
						<p class="account-review-note">
							Your subscription changes are handled personally by our account
							management team.
						</p>
					</div>
				{{else}}
					<p>
						Before you make a change, let's explore how Databox's latest AI capabilities
						could help you achieve more with your existing setup. Our team can also help
						you simplify your reporting and find a plan that better fits your needs.
					</p>

					<div class="controls2 controls2--center">
						<div class="buttons-row">
							<SubmitButtonComponent
								@text="Continue cancellation"
								@theme="grey-outlined"
								@size="big"
								@noDelay={{true}}
								@onTrigger={{this.cancel}}
							/>
							{{#if this.flowCancellationService.canRequestSetupHelp}}
								<SubmitButtonComponent
									@text="Schedule your account review"
									@size="big"
									@icon="External"
									@iconPosition={{IconPosition.AFTER}}
									@noDelay={{true}}
									@onTrigger={{this.scheduleAccountReview}}
								/>
							{{/if}}
						</div>
						<SubmitButtonComponent
							@text="I want to keep my subscription"
							@theme="grey-empty"
							@onTrigger={{this.close}}
						/>
					</div>
				{{/if}}
			{{/if}}
		</div>
	</template>
}
